import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { 
  Plus, Edit2, CheckCircle2, Circle, 
  Settings2, Power, RefreshCw, Landmark, HelpCircle 
} from 'lucide-react';

const DestinationMethods = () => {
  const [configs, setConfigs] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const initialFormState = {
    country_code: '', 
    method_name: '', 
    account_number_placeholder: '',
    requires_bank: false, 
    bank_options: [], 
    requires_account_type: false,
    active: true // <-- Nuevo campo de estado
  };

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => { fetchConfigs(); }, []);

  const fetchConfigs = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('destination_methods_config')
      .select('*')
      .order('active', { ascending: false })
      .order('country_code');
    setConfigs(data || []);
    setLoading(false);
  };

  const handleSave = async () => {
    if (!formData.country_code || !formData.method_name) {
      alert("País y Nombre del Método son obligatorios");
      return;
    }

    setLoading(true);
    try {
      const payload = { 
        ...formData, 
        country_code: formData.country_code.toUpperCase() 
      };

      if (editingId) {
        await supabase.from('destination_methods_config').update(payload).eq('id', editingId);
      } else {
        await supabase.from('destination_methods_config').insert([payload]);
      }
      
      setEditingId(null);
      setFormData(initialFormState);
      fetchConfigs();
    } catch (err) {
      console.error("Error saving:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    const { error } = await supabase
      .from('destination_methods_config')
      .update({ active: !currentStatus })
      .eq('id', id);
    
    if (!error) fetchConfigs();
  };

  const handleEdit = (config) => {
    setEditingId(config.id);
    setFormData(config);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="p-8">
      {/* FORMULARIO DE CONFIGURACIÓN */}
      <div className={`mb-10 p-8 rounded-3xl border-2 transition-all duration-500 ${editingId ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-100'} shadow-xl shadow-slate-200/50`}>
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-xl font-black flex items-center gap-3 text-slate-800 uppercase tracking-tighter">
            <Settings2 className={`w-6 h-6 ${editingId ? 'text-blue-600' : 'text-slate-400'}`} />
            {editingId ? 'Ajustar Configuración' : 'Configurar Nuevo Destino'}
          </h3>
          <button onClick={fetchConfigs} className="p-2 text-slate-400 hover:text-blue-600">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 ml-1 tracking-widest uppercase">Código País</label>
            <input placeholder="Ej: PE" className="w-full p-4 border-2 border-white rounded-2xl shadow-sm focus:border-blue-400 outline-none transition-all font-black text-lg" value={formData.country_code} onChange={e => setFormData({...formData, country_code: e.target.value})} />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 ml-1 tracking-widest uppercase">Nombre para el Cliente</label>
            <input placeholder="Ej: Pago Móvil" className="w-full p-4 border-2 border-white rounded-2xl shadow-sm focus:border-blue-400 outline-none transition-all font-bold" value={formData.method_name} onChange={e => setFormData({...formData, method_name: e.target.value})} />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 ml-1 tracking-widest uppercase flex items-center gap-1">Ayuda en el campo <HelpCircle className="w-3 h-3"/></label>
            <input placeholder="Ej: Número de teléfono" className="w-full p-4 border-2 border-white rounded-2xl shadow-sm focus:border-blue-400 outline-none transition-all" value={formData.account_number_placeholder} onChange={e => setFormData({...formData, account_number_placeholder: e.target.value})} />
          </div>
        </div>
        
        <div className="bg-white/50 p-6 rounded-2xl border-2 border-white mb-8">
          <div className="flex flex-wrap gap-10 mb-6">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input type="checkbox" checked={formData.requires_bank} onChange={e => setFormData({...formData, requires_bank: e.target.checked})} className="w-6 h-6 rounded-xl border-2 border-slate-200 checked:bg-blue-600 transition-all cursor-pointer" />
              <div className="space-y-0.5">
                <span className="font-black text-slate-700 block text-sm">¿Lista de Bancos?</span>
                <span className="text-[10px] text-slate-400 uppercase font-bold">Muestra desplegable</span>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer group">
              <input type="checkbox" checked={formData.requires_account_type} onChange={e => setFormData({...formData, requires_account_type: e.target.checked})} className="w-6 h-6 rounded-xl border-2 border-slate-200 checked:bg-blue-600 transition-all cursor-pointer" />
              <div className="space-y-0.5">
                <span className="font-black text-slate-700 block text-sm">¿Tipo de Cuenta?</span>
                <span className="text-[10px] text-slate-400 uppercase font-bold">Ahorro / Corriente</span>
              </div>
            </label>
          </div>

          {formData.requires_bank && (
            <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
              <label className="text-[10px] font-black text-slate-400 ml-1 tracking-widest flex items-center gap-1 uppercase"><Landmark className="w-3 h-3"/> Bancos Disponibles (Separar por comas)</label>
              <textarea placeholder="Banco de Venezuela, Banesco, Mercantil..." className="w-full p-4 border-2 border-white rounded-2xl focus:border-blue-400 outline-none transition-all text-sm font-bold shadow-sm" rows="2" value={formData.bank_options?.join(', ')} onChange={e => setFormData({...formData, bank_options: e.target.value.split(',').map(s => s.trim()).filter(s => s !== '')})} />
            </div>
          )}
        </div>
        
        <div className="flex justify-between items-center">
          <label className="flex items-center gap-2 cursor-pointer group">
            <div onClick={() => setFormData({...formData, active: !formData.active})} className={`w-12 h-6 rounded-full transition-all relative ${formData.active ? 'bg-green-500' : 'bg-slate-300'}`}>
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.active ? 'left-7' : 'left-1'}`}></div>
            </div>
            <span className="text-sm font-black text-slate-600 uppercase tracking-tighter">Habilitar para retiros</span>
          </label>

          <div className="flex gap-4">
            {editingId && (
              <button onClick={() => {setEditingId(null); setFormData(initialFormState);}} className="px-8 py-4 rounded-2xl font-black text-slate-400 hover:bg-white transition-all">Cancelar</button>
            )}
            <button onClick={handleSave} disabled={loading} className="px-12 py-4 rounded-2xl font-black bg-blue-600 text-white hover:bg-blue-700 shadow-xl shadow-blue-200 transition-all active:scale-95 uppercase text-sm tracking-widest">
              {loading ? 'Guardando...' : editingId ? 'Actualizar Config' : 'Crear Config'}
            </button>
          </div>
        </div>
      </div>

      {/* TABLA DE RESULTADOS */}
      <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="text-slate-400 text-[10px] font-black uppercase tracking-[2px] border-b bg-slate-50/50">
              <th className="py-6 pl-10">Status</th>
              <th>País</th>
              <th>Plataforma</th>
              <th className="text-center">Extras</th>
              <th className="text-right pr-10">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {configs.map(c => (
              <tr key={c.id} className={`group transition-all ${!c.active ? 'opacity-60 grayscale-[0.5]' : 'hover:bg-blue-50/20'}`}>
                <td className="py-6 pl-10">
                  <div className={`flex items-center gap-2 font-black text-[9px] uppercase px-3 py-1 rounded-full w-fit ${c.active ? 'bg-green-100 text-green-600' : 'bg-slate-200 text-slate-500'}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${c.active ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`}></div>
                    {c.active ? 'Visible' : 'Oculto'}
                  </div>
                </td>
                <td className="py-6 font-black text-blue-900 text-lg">{c.country_code}</td>
                <td className="py-6 font-bold text-slate-700">{c.method_name}</td>
                <td className="py-6">
                  <div className="flex justify-center gap-2">
                    {c.requires_bank && <span className="text-[9px] font-black bg-orange-100 text-orange-600 px-2 py-0.5 rounded uppercase">Banco</span>}
                    {c.requires_account_type && <span className="text-[9px] font-black bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded uppercase">Cta</span>}
                    {!c.requires_bank && !c.requires_account_type && <span className="text-[9px] font-bold text-slate-300 italic">Básico</span>}
                  </div>
                </td>
                <td className="py-6 text-right pr-10 space-x-1">
                  <button onClick={() => handleEdit(c)} className="p-3 text-slate-400 hover:text-blue-600 hover:bg-white rounded-2xl shadow-sm transition-all border border-transparent hover:border-blue-100"><Edit2 className="w-4 h-4"/></button>
                  <button 
                    onClick={() => toggleStatus(c.id, c.active)} 
                    className={`p-3 rounded-2xl transition-all border shadow-sm ${c.active ? 'text-red-400 bg-white border-red-50 hover:bg-red-50' : 'text-green-400 bg-white border-green-50 hover:bg-green-50'}`}
                    title={c.active ? "Ocultar en la App" : "Mostrar en la App"}
                  >
                    <Power className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DestinationMethods;

